/**
 * Search box suggestions (Slack-style filter chips), shared by every platform. As you type in the search field:
 * - an empty query offers every filter (`from:`, `in:`, `with:`, `has:`, `is:`, `before:`, `after:`, `on:`, `during:`);
 * - a word that starts a filter key offers those keys ("f" → `from:`; "i" → `in:`, `is:`);
 * - a filter with its colon offers values: people for `from:` / `with:` (and `from:me`), channels then people (their
 *   DM) for `in:`, the `has:` / `is:` values, and dates for `before:` / `after:` / `on:` / `during:` (today and
 *   yesterday; for `during:` also this / last month and year, and month names).
 * - a leading `-` offers the same as an exclusion, for the filters that can be negated (`-from:`, `-in:`, `-with:`,
 *   `-has:`; logic/searchQuery): "-" alone offers those keys, "-f" → `-from:`, "-from:br" → `-from:@bruno`. The menu
 *   says `exclude`, and every row's label and insert carry the `-`.
 * Choosing a key inserts it without a trailing space (so its values show next); choosing a value inserts the whole
 * filter and a space. The inserted values are what logic/searchQuery parses (`from:@handle`, or `from:"Display Name"`
 * when someone has no handle; `in:#channel`).
 */
import { msg, type MessageDescriptor } from "../i18n.ts";
import { canSeeMember, capabilitiesFor, isRestricted } from "./access.ts";
import { fold, matchScore } from "./search.ts";
import { MONTHS, normalizeDate } from "./searchQuery.ts";
import { sharedChannelIdsWith, type WorkspaceCache } from "./workspace.ts";

export type SearchFilterKey = "from" | "in" | "with" | "has" | "is" | "before" | "after" | "on" | "during";

export const SEARCH_FILTER_KEYS: SearchFilterKey[] = ["from", "in", "with", "has", "is", "before", "after", "on", "during"];

/** The filters a leading `-` can exclude (negated dates and `-is:` aren't supported; logic/searchQuery). */
export const SEARCH_EXCLUDE_KEYS: SearchFilterKey[] = ["from", "in", "with", "has"];

/** Most rows the menu shows. */
export const MAX_SEARCH_SUGGESTIONS = 9;

export type SearchSuggestPerson = { id: string; handle?: string; name: string; detail?: string; avatarUrl?: string };
export type SearchSuggestChannel = { id: string; name: string; isPrivate: boolean };

/** What the suggestions draw on: the people and channels I can see, and my local date ("YYYY-MM-DD"). */
export type SearchSuggestContext = {
  meId?: string;
  people: SearchSuggestPerson[];
  channels: SearchSuggestChannel[];
  today: string;
};

/** Also the Mail search box's filters (logic/mailQuery), which share this menu. */
export type SearchMenuKey = SearchFilterKey | "to" | "domain" | "subject" | "label" | "mailbox" | "or";

export type SearchSuggestion =
  | { kind: "filter"; key: SearchMenuKey; label: string; description: MessageDescriptor; insert: string }
  | { kind: "person"; key: SearchMenuKey; id: string; label: string; detail?: string; avatarUrl?: string; insert: string }
  | { kind: "channel"; key: SearchMenuKey; id: string; label: string; isPrivate: boolean; insert: string }
  | { kind: "value"; key: SearchMenuKey; value: string; label: string; description?: MessageDescriptor; insert: string };

/**
 * The open menu: the token [start, end) a choice replaces, the filter being completed (if any), and its rows.
 * `exclude`: the token starts with `-` (the rows are exclusions; their label and insert start with `-`).
 */
export type SearchSuggestMenu = { start: number; end: number; key?: SearchMenuKey; query: string; items: SearchSuggestion[]; exclude?: true };

export type SearchTextEdit = { text: string; caret: number };

const isSpace = (ch: string) => /\s/u.test(ch);

const FILTER_DESCRIPTIONS: Record<SearchFilterKey, MessageDescriptor> = {
  from: msg("search.filter.from"),
  in: msg("search.filter.in"),
  with: msg("search.filter.with"),
  has: msg("search.filter.has"),
  is: msg("search.filter.is"),
  before: msg("search.filter.before"),
  after: msg("search.filter.after"),
  on: msg("search.filter.on"),
  during: msg("search.filter.during"),
};

const EXCLUDE_DESCRIPTIONS: Partial<Record<SearchFilterKey, MessageDescriptor>> = {
  from: msg("search.filter.notFrom"),
  in: msg("search.filter.notIn"),
  with: msg("search.filter.notWith"),
  has: msg("search.filter.notHas"),
};

const HAS_VALUES: [string, MessageDescriptor][] = [
  ["link", msg("search.value.link")],
  ["file", msg("search.value.file")],
  ["pin", msg("search.value.pin")],
  ["reaction", msg("search.value.reaction")],
];

/** The whitespace-delimited token around `caret`: [start, end) and the part before the caret. */
export function searchToken(text: string, caret: number): { start: number; end: number; typed: string } {
  const at = Math.max(0, Math.min(caret, text.length));
  let start = at;
  while (start > 0 && !isSpace(text.charAt(start - 1))) start -= 1;
  let end = at;
  while (end < text.length && !isSpace(text.charAt(end))) end += 1;
  return { start, end, typed: text.slice(start, at) };
}

function shift(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d) + days * 86_400_000).toISOString().slice(0, 10);
}

function dateValues(key: SearchFilterKey, today: string): { value: string; description?: MessageDescriptor }[] {
  const out: { value: string; description?: MessageDescriptor }[] = [
    { value: today, description: msg("search.value.today") },
    { value: shift(today, -1), description: msg("search.value.yesterday") },
  ];
  if (key !== "during") return out;
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const ym = (yy: number, mm: number) => `${String(yy).padStart(4, "0")}-${String(mm).padStart(2, "0")}`;
  out.push(
    { value: ym(y, m), description: msg("search.value.thisMonth") },
    { value: m === 1 ? ym(y - 1, 12) : ym(y, m - 1), description: msg("search.value.lastMonth") },
    { value: String(y), description: msg("search.value.thisYear") },
    { value: String(y - 1), description: msg("search.value.lastYear") },
  );
  return out;
}

function personValue(p: SearchSuggestPerson): string {
  if (p.handle !== undefined && p.handle !== "" && !/[\s"]/u.test(p.handle)) return `@${p.handle}`;
  return `"${p.name.replace(/"/g, "")}"`;
}

function people(key: SearchFilterKey, query: string, ctx: SearchSuggestContext): SearchSuggestion[] {
  const q = query.replace(/^@/, "");
  const ranked: { item: SearchSuggestion; score: number; me: boolean; sort: string }[] = [];
  for (const p of ctx.people) {
    const score = Math.max(matchScore(q, p.name), p.handle === undefined ? 0 : matchScore(q, p.handle));
    if (score === 0) continue;
    const me = p.id === ctx.meId;
    const value = me && key === "from" ? "me" : personValue(p);
    const item: SearchSuggestion = { kind: "person", key, id: p.id, label: p.name, insert: `${key}:${value}` };
    const detail = p.handle !== undefined && p.handle !== "" ? `@${p.handle}` : p.detail;
    if (detail !== undefined && detail !== "") item.detail = detail;
    if (p.avatarUrl !== undefined) item.avatarUrl = p.avatarUrl;
    ranked.push({ item, score, me, sort: fold(p.name) });
  }
  // Best match first; me last among equals (like the composer's @ menu); then by name.
  ranked.sort((a, b) => b.score - a.score || Number(a.me) - Number(b.me) || (a.sort < b.sort ? -1 : a.sort > b.sort ? 1 : 0));
  return ranked.map((r) => r.item);
}

function channels(key: SearchFilterKey, query: string, ctx: SearchSuggestContext): SearchSuggestion[] {
  const q = query.replace(/^#/, "");
  const ranked: { item: SearchSuggestion; score: number; sort: string }[] = [];
  for (const c of ctx.channels) {
    const score = matchScore(q, c.name);
    if (score === 0) continue;
    const value = /[\s"]/u.test(c.name) ? `"#${c.name.replace(/"/g, "")}"` : `#${c.name}`;
    ranked.push({ item: { kind: "channel", key, id: c.id, label: `#${c.name}`, isPrivate: c.isPrivate, insert: `${key}:${value}` }, score, sort: fold(c.name) });
  }
  ranked.sort((a, b) => b.score - a.score || (a.sort < b.sort ? -1 : a.sort > b.sort ? 1 : 0));
  return ranked.map((r) => r.item);
}

function values(key: SearchFilterKey, query: string, list: { value: string; description?: MessageDescriptor }[]): SearchSuggestion[] {
  const q = query.toLowerCase();
  return list
    .filter((v) => v.value.startsWith(q))
    .map((v) => {
      const item: SearchSuggestion = { kind: "value", key, value: v.value, label: `${key}:${v.value}`, insert: `${key}:${v.value}` };
      if (v.description !== undefined) item.description = v.description;
      return item;
    });
}

/**
 * The suggestions for the token at `caret`, or undefined when there's nothing to offer (a quoted token, plain words,
 * an unknown key, or no matches).
 */
export function searchSuggestions(text: string, caret: number, ctx: SearchSuggestContext): SearchSuggestMenu | undefined {
  const { start, end, typed: token } = searchToken(text, caret);
  if (token.includes('"')) return undefined;
  const exclude = token.startsWith("-");
  const typed = exclude ? token.slice(1) : token;
  const keys = exclude ? SEARCH_EXCLUDE_KEYS : SEARCH_FILTER_KEYS;
  const colon = typed.indexOf(":");
  let items: SearchSuggestion[];
  let key: SearchFilterKey | undefined;
  let query: string;
  if (colon < 0) {
    query = typed;
    const q = typed.toLowerCase();
    // Every filter on an empty query; a partial key elsewhere (a blank token mid-query offers nothing).
    if (q === "" && !exclude && text.trim() !== "") return undefined;
    items = keys.filter((k) => k.startsWith(q)).map((k) => ({
      kind: "filter",
      key: k,
      label: `${k}:`,
      description: (exclude ? EXCLUDE_DESCRIPTIONS[k] : undefined) ?? FILTER_DESCRIPTIONS[k],
      insert: `${k}:`,
    }));
  } else {
    const k = typed.slice(0, colon).toLowerCase() as SearchFilterKey;
    if (!keys.includes(k)) return undefined;
    key = k;
    query = typed.slice(colon + 1);
    switch (k) {
      case "from":
      case "with":
        items = people(k, query, ctx);
        break;
      case "in":
        items = [...channels(k, query, ctx), ...people(k, query, ctx).filter((p) => p.kind !== "person" || p.id !== ctx.meId)];
        break;
      case "has":
        items = values(k, query, HAS_VALUES.map(([value, description]) => ({ value, description })));
        break;
      case "is":
        items = values(k, query, [
          { value: "thread", description: msg("search.value.thread") },
          { value: "saved", description: msg("search.value.saved") },
        ]);
        break;
      default: {
        const list = dateValues(k, ctx.today);
        if (k === "during") for (const m of MONTHS) list.push({ value: m });
        items = values(k, query, list);
        // Month names only once a letter is typed (dates first otherwise).
        if (k === "during" && !/^[a-z]/iu.test(query)) items = items.filter((i) => i.kind !== "value" || /^\d/.test(i.value));
      }
    }
  }
  if (items.length === 0) return undefined;
  if (exclude) items = items.map((i) => ({ ...i, label: i.kind === "filter" || i.kind === "value" ? `-${i.label}` : i.label, insert: `-${i.insert}` }));
  const menu: SearchSuggestMenu = { start, end, query, items: items.slice(0, MAX_SEARCH_SUGGESTIONS) };
  if (key !== undefined) menu.key = key;
  if (exclude) menu.exclude = true;
  return menu;
}

/**
 * Replaces the menu's token with the chosen row. A filter key leaves the caret right after its colon; a value adds a
 * space (reusing a following one) and puts the caret after it.
 */
export function applySearchSuggestion(text: string, menu: Pick<SearchSuggestMenu, "start" | "end">, item: SearchSuggestion): SearchTextEdit {
  const before = text.slice(0, menu.start);
  const after = text.slice(menu.end);
  if (item.kind === "filter") return { text: `${before}${item.insert}${after}`, caret: menu.start + item.insert.length };
  const space = isSpace(after.charAt(0)) ? "" : " ";
  return { text: `${before}${item.insert}${space}${after}`, caret: menu.start + item.insert.length + 1 };
}

/** The date filters, which the menu pairs with a date picker. */
export const SEARCH_DATE_KEYS: SearchFilterKey[] = ["before", "after", "on", "during"];

/**
 * A date picked in the menu's date picker for a date filter (`key` one of SEARCH_DATE_KEYS): replaces the menu's
 * token with `key:YYYY-MM-DD` (with `month`, `key:YYYY-MM`: Slack's "during" a month) like a chosen value.
 * Undefined when `key` isn't a date filter or `date` isn't a real day.
 */
export function applySearchDate(
  text: string,
  menu: Pick<SearchSuggestMenu, "start" | "end">,
  key: SearchFilterKey,
  date: string,
  month = false,
): SearchTextEdit | undefined {
  const day = normalizeDate(date);
  if (!SEARCH_DATE_KEYS.includes(key) || day === undefined) return undefined;
  const value = month ? day.slice(0, 7) : day;
  return applySearchSuggestion(text, menu, { kind: "value", key, value, label: `${key}:${value}`, insert: `${key}:${value}` });
}

/**
 * The suggestion context from the offline cache at `now`: people I may see (not deactivated; `can.seeDirectory`),
 * channels I can read (public or joined; restricted members only joined ones, never archived), my local date.
 */
export function searchSuggestContext(cache: WorkspaceCache, now: number, offsetMinutes: number): SearchSuggestContext {
  const me = cache.members[cache.meId];
  const caps = me === undefined ? undefined : capabilitiesFor(me, now);
  const filtered = me !== undefined && caps !== undefined && caps.seeDirectory !== "all";
  const restricted = me !== undefined && isRestricted(me);
  const people: SearchSuggestPerson[] = [];
  for (const id of Object.keys(cache.members).toSorted()) {
    const m = cache.members[id]!;
    if (m.deactivated) continue;
    if (filtered && !canSeeMember(me!, m, sharedChannelIdsWith(cache, id), now)) continue;
    const p: SearchSuggestPerson = { id, name: m.displayName };
    if (m.handle !== undefined) p.handle = m.handle;
    if (m.fullName !== m.displayName && m.fullName !== "") p.detail = m.fullName;
    if (m.avatarUrl !== undefined) p.avatarUrl = m.avatarUrl;
    people.push(p);
  }
  const channels: SearchSuggestChannel[] = [];
  for (const id of Object.keys(cache.channels).toSorted()) {
    const c = cache.channels[id]!;
    if ((c.kind !== "public" && c.kind !== "private") || c.archived) continue;
    const member = cache.memberships[id] !== undefined;
    if (!member && (c.kind === "private" || restricted)) continue;
    channels.push({ id, name: c.name, isPrivate: c.kind === "private" });
  }
  return { meId: cache.meId, people, channels, today: new Date(now + offsetMinutes * 60_000).toISOString().slice(0, 10) };
}
